import React from 'react';
import { Box, Typography } from '@mui/material';

import THEME from '../../constants/theme';

/**
 * BlogContentParser Component
 * Parses blog content and applies styling:
 * - __text__ becomes underlined with blue color and semi-bold font
 * - - Item creates bullet points with styled formatting
 * - #TABLE_START to #TABLE_END creates styled data tables
 * - \n\n creates paragraph breaks
 * - \n creates line breaks
 *
 * @param {string} text - The text string to parse and apply underline formatting
 * @returns {React.Component} - Formatted text with underline styling
 */
const renderUnderlinedText = text => {
  const parts = text.split(/(__[^_]+__)/);
  return parts.map((part, partIndex) => {
    if (part.match(/^__.*__$/)) {
      const innerText = part.slice(2, -2);
      return (
        <span
          key={partIndex}
          style={{
            textDecoration: 'underline',
            textDecorationColor: THEME.COLORS.PRIMARY_BLUE,
            textUnderlineOffset: '2px',
            textDecorationThickness: '1.5px',
            fontWeight: 600,
            color: 'inherit'
          }}
        >
          {innerText}
        </span>
      );
    }
    return <span key={partIndex}>{part}</span>;
  });
};

export const parseContent = content => {
  // First, extract and process tables
  const tableRegex = /#TABLE_START([\s\S]*?)#TABLE_END/g;
  const tables = [];
  let tableMatch;
  let tableIndex = 0;

  while ((tableMatch = tableRegex.exec(content)) !== null) {
    const tableContent = tableMatch[1].trim();
    const rows = tableContent.split('\n').filter(row => row.trim());
    if (rows.length > 0) {
      const headers = rows[0].split('|').map(h => h.trim());
      const data = rows
        .slice(1)
        .map(row => row.split('|').map(cell => cell.trim()));
      tables.push({ headers, data, placeholder: `__TABLE_${tableIndex}__` });
      tableIndex++;
    }
  }

  // Replace table markers with placeholders
  let processedContent = content;
  tables.forEach((table, idx) => {
    processedContent = processedContent.replace(
      /#TABLE_START[\s\S]*?#TABLE_END/,
      `__TABLE_${idx}__`
    );
  });

  // Split content by double newlines for paragraphs
  const paragraphs = processedContent.split('\n\n');

  return paragraphs.map((paragraph, paragraphIndex) => {
    // Check if paragraph is a blockquote (starts with >)
    if (paragraph.trim().startsWith('>')) {
      const quoteText = paragraph.trim().replace(/^>\s*/, '').replace(/"/g, '');
      return (
        <Box
          key={paragraphIndex}
          sx={{
            my: 3,
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 2.5 },
            textAlign: 'center',
            position: 'relative',
            borderLeft: `3px solid ${THEME.COLORS.PRIMARY_BLUE}`,
            borderRight: `3px solid ${THEME.COLORS.PRIMARY_BLUE}`,
            backgroundColor: 'rgba(30, 82, 243, 0.03)',
            borderRadius: '6px'
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.15rem' },
              fontWeight: 500,
              fontStyle: 'italic',
              color: THEME.COLORS.NEUTRAL_BLACK,
              lineHeight: 1.6,
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY
            }}
          >
            {quoteText}
          </Typography>
        </Box>
      );
    }

    // Check if paragraph is a table placeholder
    if (paragraph.trim().match(/^__TABLE_\d+__$/)) {
      const tableNum = parseInt(paragraph.match(/\d+/)[0]);
      if (tables[tableNum]) {
        const { headers, data } = tables[tableNum];
        return (
          <Box
            key={paragraphIndex}
            sx={{
              mb: 3,
              overflowX: 'auto',
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                border: `1px solid rgba(30, 82, 243, 0.2)`
              },
              '& th': {
                backgroundColor: THEME.COLORS.PRIMARY_BLUE,
                color: THEME.COLORS.NEUTRAL_WHITE,
                padding: '12px 16px',
                textAlign: 'left',
                fontWeight: 600,
                fontSize: '0.9rem',
                borderBottom: `2px solid ${THEME.COLORS.PRIMARY_BLUE}`
              },
              '& td': {
                padding: '12px 16px',
                borderBottom: `1px solid rgba(30, 82, 243, 0.1)`,
                fontSize: '0.9rem',
                color: THEME.COLORS.NEUTRAL_BLACK_LIGHT
              },
              '& tbody tr': {
                '&:nth-of-type(odd)': {
                  backgroundColor: 'rgba(30, 82, 243, 0.02)'
                },
                '&:hover': {
                  backgroundColor: 'rgba(30, 82, 243, 0.06)',
                  transition: `background-color ${THEME.TRANSITIONS.MEDIUM}`
                }
              },
              '& tbody tr:last-child td': {
                borderBottom: 'none'
              }
            }}
          >
            <table>
              <thead>
                <tr>
                  {headers.map((header, idx) => (
                    <th key={idx}>{renderUnderlinedText(header)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, rowIdx) => (
                  <tr key={rowIdx}>
                    {row.map((cell, cellIdx) => (
                      <td key={cellIdx}>{renderUnderlinedText(cell)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        );
      }
    }

    // Check if this paragraph contains bullet points
    const lines = paragraph.split('\n');
    const isBulletList = lines.some(line => line.trim().startsWith('- '));

    if (isBulletList) {
      // Process as bullet list
      const bulletItems = lines
        .filter(line => line.trim().startsWith('- '))
        .map(line => line.trim().substring(2));

      return (
        <Box
          key={paragraphIndex}
          component='ul'
          sx={{
            mb: 2,
            pl: 2.5,
            listStyle: 'none',
            '& li': {
              mb: 1,
              position: 'relative',
              paddingLeft: 2,
              '&::before': {
                content: '"•"',
                position: 'absolute',
                left: 0,
                color: THEME.COLORS.PRIMARY_BLUE,
                fontWeight: 'bold',
                fontSize: '1.2em'
              }
            }
          }}
        >
          {bulletItems.map((item, itemIndex) => (
            <li key={itemIndex}>{renderUnderlinedText(item)}</li>
          ))}
        </Box>
      );
    }

    // Process as regular paragraphs
    const formattedLines = lines.map((line, lineIndex) => (
      <div key={lineIndex}>{renderUnderlinedText(line)}</div>
    ));

    return (
      <Box key={paragraphIndex} sx={{ mb: 2 }}>
        {formattedLines}
      </Box>
    );
  });
};

/**
 * BlogContentParser Component - Wrapper for easy usage
 * Applies project typography and spacing
 *
 * @param {string} content - The content to parse and display
 * @returns {React.Component} - Formatted and styled content
 */
const BlogContentParser = ({ content }) => {
  return (
    <Typography
      component='div'
      sx={{
        fontSize: { xs: '0.95rem', sm: '1rem', md: '1.05rem' },
        lineHeight: 1.75,
        color: THEME.COLORS.NEUTRAL_BLACK_LIGHT,
        fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
        '& > div': {
          marginBottom: '1.5rem'
        },
        '& > div:last-child': {
          marginBottom: 0
        }
      }}
    >
      {parseContent(content)}
    </Typography>
  );
};

export default BlogContentParser;
